import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  Button,
  Card,
  Flag,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { useT, T } from '@transifex/react'
import {
  usePMOFFeature,
  usePMOFTerminalSaveFeature,
} from '@vend/business-components'
import { getRetailer } from '@vend/utilities'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectCustomer,
  selectSaleId,
  selectSaleLocalId,
  selectSalePayments,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { selectTrainingModeEnabled } from '../../../../state/slices/posState/posStateSlice'
import { isValidCustomer } from '../../../../functions/sale'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import {
  useRegisterManager,
  useUserManager,
  useVdTracking,
  useWebRegisterStatusManager,
} from '../../../../ng-compat/hooks'
import { PERMISSIONS } from '../../../../constants'
import { useFetchPairing } from '../../../payments/lspay-payment-modal/hooks/useFetchPairing'
import { Payment } from '../../../../models/payment'
import { LSPAY_PRODUCT_VERSION } from '../../../payments/lspay-payment-modal/helpers/lspayConstants'
import { TerminalCardSaveModal } from './TerminalCardSaveModal'

type SaveState = 'idle' | 'saving' | 'succeeded'

const TerminalCardSavePromptTracker: FunctionComponent<{
  onShown: () => void
}> = ({ onShown }) => {
  const tracked = useRef(false)

  useEffect(() => {
    if (!tracked.current) {
      onShown()
      tracked.current = true
    }
  }, [onShown])

  return null
}

const BANNER_DISMISSED_KEY = 'terminal_card_save_banner_dismissed'

/**
 * Find the first terminal payment on the sale that has card data.
 */
function findTerminalPayment(payments: Payment[]): Payment | undefined {
  return payments.find(
    p => p.card?.brand && p.card?.lastFour && p.card?.entryMethod !== 'KEYED'
  )
}

export const TerminalCardSave: FunctionComponent = () => {
  const t = useT()
  const customer = useAppSelector(selectCustomer)
  const saleId = useAppSelector(selectSaleId)
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const payments = useAppSelector(selectSalePayments)
  const isTrainingMode = useAppSelector(selectTrainingModeEnabled)
  const registerManager = useRegisterManager()
  const userManager = useUserManager()
  const vdTracking = useVdTracking()
  const loggedInUserId = userManager?.getLoggedInUser()?.id
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const { isEnabled: pmofSettingEnabled } = usePMOFFeature()
  const { isEnabled: terminalSaveEnabled } = usePMOFTerminalSaveFeature({
    skip: !pmofSettingEnabled,
  })

  const featureFlagEnabled = getFeatureFromCache('enable_customer_saved_cards')
  const userPermissionGranted = userManager?.hasPermission(
    PERMISSIONS.LSPAY_PMOF_ADD_EDIT_USE
  )

  const register = registerManager?.getActiveRegister()
  const { pairing } = useFetchPairing(register?.id)

  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [retailerId, setRetailerId] = useState<string | null>(null)
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem(BANNER_DISMISSED_KEY) === 'true'
  )

  const dismissBanner = () => {
    setBannerDismissed(true)
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true')
  }

  useEffect(() => {
    getRetailer()
      .then(retailer => {
        setRetailerId(retailer.id)
      })
      .catch(() => {
        // retailerId stays null, component won't render save card UI
      })
  }, [])

  const terminalPayment = findTerminalPayment(payments)

  const enabled =
    featureFlagEnabled &&
    pmofSettingEnabled &&
    terminalSaveEnabled &&
    userPermissionGranted &&
    !isTrainingMode &&
    webRegisterStatusManager?.isOnline() // Saving a card requires calling the swiper API

  if (!enabled || !isValidCustomer(customer) || !retailerId) {
    return null
  }
  if (!terminalPayment?.card?.brand || !terminalPayment?.card?.lastFour) {
    return null
  }

  if (!pairing?.deviceID || !pairing?.pli) {
    return null
  }

  if (saveState === 'succeeded') {
    return null
  }

  const customerName = customer.name || customer.first_name || ''

  const handleSuccess = () => {
    setSaveState('succeeded')
    ToastNotifications.success(
      t("Card added to {customerName}'s profile", {
        customerName,
        _context: 'Success toast when card is saved on file',
      })
    )
  }

  const handleError = (message: string) => {
    setSaveState('idle')
    ToastNotifications.negative(
      message ||
        t('Card save failed', {
          _context: 'Error toast when card save fails',
        })
    )
  }

  const handleCancelled = () => {
    setSaveState('idle')
    ToastNotifications.negative(
      t('Card save cancelled', {
        _context: 'Toast when card save is cancelled by customer on terminal',
      })
    )
  }

  const handleSaveCard = () => {
    vdTracking.trackUserEvent({
      eventName: 'Terminal Card Save Button Clicked',
      metadata: {
        saleId: saleId || saleLocalId,
        paymentId: terminalPayment.id || terminalPayment.localId,
      },
    })
    setSaveState('saving')
  }

  return (
    <>
      {/* TODO: Replace with Helios 2.0 "Notice" component when available */}
      <Card className="vd-mt10">
        <TerminalCardSavePromptTracker
          onShown={() =>
            vdTracking.trackUserEvent({
              eventName: 'Terminal Card Save Prompt Shown',
              metadata: {
                saleId: saleId || saleLocalId,
                paymentId: terminalPayment.id || terminalPayment.localId,
              },
            })
          }
        />
        {!bannerDismissed && (
          <>
            <div className="vd-pa5 vd-flex vd-flex--align-start terminal-card-save-banner">
              <span style={{ flexShrink: 0 }} className="vd-mr2">
                <Flag
                  content={t('New', {
                    _context: 'Badge for new terminal card save feature',
                  })}
                  category="info"
                />
              </span>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div className="vd-text--bold">
                  <T
                    _str="Save cards for faster checkouts"
                    _context="Title for terminal card save info banner"
                  />
                </div>
                <div className="vd-text--secondary vd-text--sub">
                  <T
                    _str="Use your terminal to securely save cards and process future orders with a single click. You can turn this off anytime in Settings."
                    _context="Description for terminal card save info banner"
                  />
                </div>
              </div>
              <button
                className="vd-btn--reset vd-ml4"
                style={{ flexShrink: 0 }}
                onClick={dismissBanner}
                aria-label={t('Dismiss', {
                  _context: 'Dismiss terminal card save banner',
                })}
              >
                &times;
              </button>
            </div>
            <hr className="terminal-card-save-banner-divider" />
          </>
        )}
        <div className="vd-ma5 vd-flex vd-flex--align-center vd-flex--justify-between">
          <span>
            <T
              _str="Save card for future purchases"
              _context="Label for save card on file on the confirmation screen"
            />
          </span>
          <Button variant="supplementary" onClick={handleSaveCard}>
            <T
              _str="Save card"
              _context="Button to initiate saving card on file via terminal"
            />
          </Button>
        </div>
      </Card>
      {saveState === 'saving' &&
        ReactDOM.createPortal(
          <TerminalCardSaveModal
            merchantReference={retailerId}
            customerReference={customer.id}
            customerName={customerName}
            pspTerminalId={pairing.deviceID}
            terminalLabel={pairing.deviceLabel}
            pli={pairing.pli}
            saleId={saleId || undefined}
            createdBy={loggedInUserId}
            metadata={{
              salesChannel: 'IN_STORE',
              deviceId: register?.id || '',
              productVersion: LSPAY_PRODUCT_VERSION,
            }}
            onSuccess={handleSuccess}
            onError={handleError}
            onCancelled={handleCancelled}
          />,
          document.body
        )}
    </>
  )
}
